import { mapsForMode } from '../content/map';
import type { MapId, MatchMode } from '../content/types';
import { Select } from './components/Select';

export function MapPicker({
  mode,
  selected,
  onSelect,
  disabled = false,
  votes,
}: {
  mode: MatchMode;
  selected: MapId;
  onSelect: (mapId: MapId) => void;
  disabled?: boolean;
  votes?: Partial<Record<MapId, number>>;
}) {
  const maps = mapsForMode(mode);
  const map = maps.find((candidate) => candidate.id === selected) ?? maps[0]!;
  return (
    <div className="mapselector" aria-label="Battlefield">
      <label className="mapselectrow">
        <span>Map</span>
        <Select
          ariaLabel="Battlefield"
          value={map.id}
          disabled={disabled}
          options={maps.map((candidate) => {
            const voteCount = votes?.[candidate.id] ?? 0;
            return {
              value: candidate.id,
              label: candidate.name,
              hint: votes ? `${voteCount} vote${voteCount === 1 ? '' : 's'}` : undefined,
            };
          })}
          onChange={(id) => onSelect(id as MapId)}
        />
      </label>
      <div className="mapcard selected previewonly" aria-live="polite">
        <svg className="mappreview" viewBox={`0 0 ${map.width} ${map.height}`} aria-hidden="true">
          <rect width={map.width} height={map.height} rx="6" className="map-ground" />
          {map.rocks.map((rock, index) => (
            <rect key={`r-${index}`} x={rock.pos.x - rock.w / 2} y={rock.pos.y - rock.h / 2} width={rock.w} height={rock.h} rx="2" className="map-rock" />
          ))}
          {map.ruins.map((ruin, index) => (
            <rect key={`u-${index}`} x={ruin.pos.x - 2} y={ruin.pos.y - 4} width="4" height="8" className="map-ruin" />
          ))}
          {map.deposits.map((deposit, index) => (
            <circle key={`d-${index}`} cx={deposit.pos.x} cy={deposit.pos.y} r="2.3" className={`map-deposit ${deposit.type}`} />
          ))}
          {map.sites.map((site) => (
            <rect key={site.id} x={site.pos.x - 3.2} y={site.pos.y - 3.2} width="6.4" height="6.4" transform={`rotate(45 ${site.pos.x} ${site.pos.y})`} className="map-site" />
          ))}
          {map.corePositions.map((core, index) => (
            <circle key={`c-${index}`} cx={core.x} cy={core.y} r="5" className={`map-core player-${index}`} />
          ))}
        </svg>
        <span className="mapcard-title">{map.name}</span>
        <span className="mapcard-description">{map.description}</span>
        {votes && <span className="mapcard-votes">Your vote · {votes[map.id] ?? 0} total</span>}
      </div>
    </div>
  );
}
